import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import { Document } from '@contentful/rich-text-types'
import DOMPurify from 'dompurify'
import { marked, Renderer, type Tokens } from 'marked'

// Every renderer below emits HTML that flows into `v-html`, so it must pass
// through DOMPurify first. The sources are trusted CMS content today, but
// sanitizing at the rendering boundary removes the standing XSS risk and means
// no individual call site has to remember to sanitize.
//
// `afterSanitizeAttributes` re-applies `rel="noopener noreferrer"` to any link
// that opens in a new tab, so the guarantee survives sanitization independently
// of which renderer produced the `target="_blank"` (the marked
// `ExternalLinkRenderer` sets it; rich text never does, today).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

// `target` is preserved (ADD_ATTR) so the new-tab behavior set by the marked
// renderer isn't stripped.
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })
}

export function renderRichTextToHtml(richTextContent: Document) {
  return sanitizeHtml(documentToHtmlString(richTextContent))
}

// Only external http(s) links should open in a new tab. In-page anchors
// (#fragment), relative/internal links, and non-web schemes (mailto:, tel:)
// resolve to the current origin or fail to parse, so they keep their default
// in-tab behavior and aren't broken by a stray blank tab.
export function isExternalHttpUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin)
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      url.origin !== window.location.origin
    )
  } catch {
    return false
  }
}

// Renderer that opens only external http(s) links in a new tab with
// `noopener,noreferrer` (see isExternalHttpUrl).
class ExternalLinkRenderer extends Renderer {
  link(token: Tokens.Link): string {
    const html = super.link(token)
    if (!isExternalHttpUrl(token.href)) return html
    return html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ')
  }
}

// Single shared renderer, instantiated once at module load and reused for
// every render. `breaks: true` maps single newlines to <br> (GFM line breaks).
const markdownRenderer = new ExternalLinkRenderer()

export function renderMarkdownToHtml(markdownContent: string): string {
  const html = marked.parse(markdownContent, { renderer: markdownRenderer, breaks: true }) as string
  return sanitizeHtml(html)
}

export function getTextContentItemValueFromContentModule(
  contentModule,
  identifier: string,
  fallbackValue: string | null = null
) {
  const contentItem = contentModule?.contentItems?.find(
    (item) => item.fields.identifier === identifier
  )
  return contentItem?.fields.value || fallbackValue
}

export function getRichTextContentItemValueFromContentModule(contentModule, identifier: string) {
  const contentItem = contentModule?.contentItems?.find(
    (item) => item.fields.identifier === identifier
  )
  return contentItem?.fields.value
}

export function getMediaContentItemValueFromContentModule(contentModule, identifier: string) {
  const contentItem = contentModule?.contentItems?.find(
    (item) => item.fields.identifier === identifier
  )
  return contentItem?.fields.value
}

// Returns a getter bound to `contentModule` that resolves a text content item
// by identifier, falling back to `fallback` when the module or item is absent.
// Lets per-feature `resolve…Content` functions map fields without each
// re-declaring the same `(id, fallback) => getText…(module, id, fallback)`
// closure.
export function makeContentModuleGetter(contentModule) {
  return (identifier: string, fallback: string): string =>
    getTextContentItemValueFromContentModule(contentModule, identifier, fallback)
}

// Substitutes `##VarName##` markers in `template` using values from `vars`.
// Unmatched markers, and markers whose value is null or undefined, are
// preserved verbatim so they're visible during dev / QA and easy to grep for.
// Shared by the Senior feature `content.ts` modules and the `src/content/` data
// objects.
export function substitute(
  template: string,
  vars: Record<string, string | number | null | undefined>
): string {
  return template.replace(/##(\w+)##/g, (_match, key) => {
    const value = vars[key]
    return value == null ? `##${key}##` : String(value)
  })
}
